import { useState } from 'react';
import { Box, Button, Stack, Typography, Menu, MenuItem, useTheme } from '@mui/material';
import type { Theme } from '@mui/material';
import dayjs from 'dayjs';

import { trackDemoEvent } from './demoTracking';

import ChevronDownIcon from 'modules/common/components/Icons/ChevronDownIcon';
import ChevronUpIcon from 'modules/common/components/Icons/ChevronUpIcon';

/**
 * Shared building blocks for the demo's Accounting Overview and Home pages: the four
 * "Financial Overview" tiles (P&L, Cash Flow, Expenses, Income) as a grid with a per-tile
 * period switcher. Tasks on both pages come from the real member task framework
 * (`TaskCarousel`), not from here.
 */

type BarColor = 'success' | 'primary' | 'warning' | 'info' | 'error';
type Period = 'year' | 'month' | 'lastMonth' | 'lastYear';

const PERIOD_OPTIONS: Period[] = ['year', 'month', 'lastMonth', 'lastYear'];
const PERIOD_LABEL: Record<Period, string> = {
    year: 'This Year',
    month: 'This Month',
    lastMonth: 'Last Month',
    lastYear: 'Last Year',
};

export interface ExpenseCategory {
    name: string;
    amount: number;
    color: BarColor;
}

export interface OverviewBlob {
    cash_in: number;
    cash_out: number;
    change_in_cash: number;
    expense_categories: ExpenseCategory[];
    monthly_income: number[];
}

const currency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

const currencyCents = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

function getColor(theme: Theme, color: BarColor) {
    return theme.palette[color].main;
}

function scaleByPeriod(ytd: number, mtd: number, period: Period): number {
    switch (period) {
        case 'month':
            return mtd;
        case 'lastMonth':
            return Math.round(mtd * 1.12);
        case 'lastYear':
            return Math.round(ytd * 2.35);
        case 'year':
        default:
            return ytd;
    }
}

/**
 * Side-aware scaling for paired bars (Income/Expense, Cash In/Cash Out). Without this the
 * inflow and outflow scale by the same factor each period, so income/max and expense/max
 * stay constant and the bar widths don't visibly change. Using different multipliers per
 * side per period also varies the inflow-vs-outflow ratio (e.g. a particularly good month,
 * a prior year with thinner margins) — that ratio shift is what makes the smaller bar move.
 */
function scaleSide(ytd: number, mtd: number, period: Period, side: 'inflow' | 'outflow'): number {
    const inflowBias = side === 'inflow';
    switch (period) {
        case 'month':
            // Strong month — income up, expenses below trailing ratio.
            return Math.round(mtd * (inflowBias ? 1.15 : 0.7));
        case 'lastMonth':
            // Weaker month — income soft, expenses ~normal.
            return Math.round(mtd * (inflowBias ? 0.9 : 1.05));
        case 'lastYear':
            // Prior year overall larger but expenses grew faster YoY.
            return Math.round(ytd * (inflowBias ? 2.0 : 2.5));
        case 'year':
        default:
            return ytd;
    }
}

/**
 * Headroom above the larger paired bar, per period. Drives how full the chart looks:
 *   - tight headroom (Last Year) → dominant bar nearly fills the chart (~95%)
 *   - wide headroom (This Month) → dominant bar sits lower (~62%), so the period feels smaller
 * Without this, max = larger value, the dominant bar always pegs at 100%, and it never
 * visibly moves when filters change.
 */
function periodHeadroom(period: Period): number {
    switch (period) {
        case 'month':
            return 1.6;
        case 'lastMonth':
            return 1.45;
        case 'lastYear':
            return 1.05;
        case 'year':
        default:
            return 1.2;
    }
}

function PeriodMenu({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    return (
        <>
            <Button
                variant="text"
                size="small"
                color="inherit"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                endIcon={open ? <ChevronUpIcon fontSize="small" /> : <ChevronDownIcon fontSize="small" />}
                sx={{
                    color: 'text.secondary',
                    fontWeight: 400,
                    px: 1,
                    minWidth: 0,
                    '&:hover': { bgcolor: 'action.hover' },
                }}>
                {PERIOD_LABEL[value]}
            </Button>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                {PERIOD_OPTIONS.map((p) => (
                    <MenuItem
                        key={p}
                        selected={p === value}
                        onClick={() => {
                            onChange(p);
                            setAnchorEl(null);
                        }}>
                        {PERIOD_LABEL[p]}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

function CardShell({
    title,
    period,
    onPeriodChange,
    children,
}: {
    title: string;
    period: Period;
    onPeriodChange: (p: Period) => void;
    children: React.ReactNode;
}) {
    const handlePeriodChange = (p: Period) => {
        // Fires for all four tiles — `title` identifies which one.
        trackDemoEvent('Demo Tile Interacted', { tile: title, period: p });
        onPeriodChange(p);
    };
    return (
        <Box
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                p: 3,
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                minWidth: 0,
            }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">{title}</Typography>
                <PeriodMenu value={period} onChange={handlePeriodChange} />
            </Stack>
            {children}
        </Box>
    );
}

// Paired-bar rows for Profit + Loss and Cash Flow. Matches the real overview: inflows
// (Income, Cash In) render purple on a light-lavender track; outflows (Expenses, Cash Out)
// render black on a grey track. Labels sit inside the bar (white once the fill covers them).
function ProgressBar({
    label,
    value,
    total,
    flow,
}: {
    label: string;
    value: number;
    total: number;
    flow: 'inflow' | 'outflow';
}) {
    const safeTotal = total > 0 ? total : 1;
    const pct = Math.max(2, Math.min(100, (value / safeTotal) * 100));
    const fill = flow === 'inflow' ? 'primary.main' : 'common.black';
    const track = flow === 'inflow' ? 'primary.lightHover' : 'grey.100';
    const growTransition = 'width 0.55s cubic-bezier(0.4, 0, 0.2, 1)';
    const labelSx = {
        position: 'absolute',
        top: '50%',
        left: 16,
        transform: 'translateY(-50%)',
        fontWeight: 500,
        whiteSpace: 'nowrap',
    } as const;
    return (
        <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
            <Box
                sx={{
                    flexGrow: 1,
                    height: 44,
                    bgcolor: track,
                    borderRadius: 1.5,
                    position: 'relative',
                    overflow: 'hidden',
                    minWidth: 0,
                }}>
                <Box
                    sx={{ position: 'absolute', inset: 0, width: `${pct}%`, bgcolor: fill, transition: growTransition }}
                />
                {/* Label read on the track (dark), then a copy clipped to the fill (white) so it's
                    legible at any fill level — including when the bar edge sits mid-label. */}
                <Typography variant="body2" sx={{ ...labelSx, color: 'text.secondary' }}>
                    {label}
                </Typography>
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: `${pct}%`,
                        overflow: 'hidden',
                        transition: growTransition,
                    }}>
                    <Typography variant="body2" sx={{ ...labelSx, color: 'common.white' }}>
                        {label}
                    </Typography>
                </Box>
            </Box>
            <Typography
                variant="subtitle1"
                sx={{ minWidth: 96, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {currencyCents(value)}
            </Typography>
        </Stack>
    );
}

function ProfitLossCard({
    ytdIncome,
    ytdExpenses,
    mtdIncome,
    mtdExpenses,
}: {
    ytdIncome: number;
    ytdExpenses: number;
    mtdIncome: number;
    mtdExpenses: number;
}) {
    const [period, setPeriod] = useState<Period>('year');
    const income = scaleSide(ytdIncome, mtdIncome, period, 'inflow');
    const expenses = scaleSide(ytdExpenses, mtdExpenses, period, 'outflow');
    const net = income - expenses;
    const max = Math.max(income, expenses, 1) * periodHeadroom(period);
    return (
        <CardShell title="Profit + Loss" period={period} onPeriodChange={setPeriod}>
            <Box>
                <Typography variant="h4">{currency(net)}</Typography>
                <Typography variant="body2" color="text.secondary">
                    Net Income
                </Typography>
            </Box>
            <Stack spacing={1.5}>
                <ProgressBar label="Income" value={income} total={max} flow="inflow" />
                <ProgressBar label="Expenses" value={expenses} total={max} flow="outflow" />
            </Stack>
        </CardShell>
    );
}

function CashFlowCard({
    ytdCashIn,
    ytdCashOut,
    mtdIncome,
    mtdExpenses,
}: {
    ytdCashIn: number;
    ytdCashOut: number;
    mtdIncome: number;
    mtdExpenses: number;
}) {
    const [period, setPeriod] = useState<Period>('year');
    const cashIn = scaleSide(ytdCashIn, mtdIncome, period, 'inflow');
    const cashOut = scaleSide(ytdCashOut, mtdExpenses, period, 'outflow');
    const change = cashIn - cashOut;
    const max = Math.max(cashIn, cashOut, 1) * periodHeadroom(period);
    return (
        <CardShell title="Cash Flow" period={period} onPeriodChange={setPeriod}>
            <Box>
                <Typography variant="h4">{currency(change)}</Typography>
                <Typography variant="body2" color="text.secondary">
                    Change in Cash
                </Typography>
            </Box>
            <Stack spacing={1.5}>
                <ProgressBar label="Cash In" value={cashIn} total={max} flow="inflow" />
                <ProgressBar label="Cash Out" value={cashOut} total={max} flow="outflow" />
            </Stack>
        </CardShell>
    );
}

function ExpensesCard({ ytdTotal, mtd, categories }: { ytdTotal: number; mtd: number; categories: ExpenseCategory[] }) {
    const theme = useTheme();
    const [period, setPeriod] = useState<Period>('year');
    const total = scaleByPeriod(ytdTotal, mtd, period);
    const factor = ytdTotal > 0 ? total / ytdTotal : 1;
    const monthLabel = dayjs().format('MMM');
    return (
        <CardShell title="Expenses" period={period} onPeriodChange={setPeriod}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={2}>
                <Box>
                    <Typography variant="h4">{currency(total)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Total Expenses
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h4">{currency(mtd)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {monthLabel} Total
                    </Typography>
                </Box>
            </Stack>
            <Stack spacing={1.5}>
                {categories.map((c) => (
                    <Stack key={c.name} direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getColor(theme, c.color) }} />
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            {c.name}
                        </Typography>
                        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {currencyCents(c.amount * factor)}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </CardShell>
    );
}

function IncomeCard({ ytdTotal, mtd, monthly }: { ytdTotal: number; mtd: number; monthly: number[] }) {
    const theme = useTheme();
    const [period, setPeriod] = useState<Period>('year');
    const total = scaleByPeriod(ytdTotal, mtd, period);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthLabel = dayjs().format('MMM');
    const currentMonthIdx = dayjs().month();
    // Shape the monthly bars per period so switching filters produces visible animation,
    // not just a number change at the top of the card.
    //   year:      ytd snapshot — earlier months filled, later months empty (current)
    //   lastYear:  imagined full prior year — all 12 months populated, slightly varied
    //   month:     only current month
    //   lastMonth: only previous month
    const monthlyForPeriod: number[] = (() => {
        if (period === 'lastYear') {
            return months.map((_m, i) => {
                // Use the YTD shape if we have it, otherwise pad. Then scale up ~2x for last year.
                const base = monthly[i] || monthly[currentMonthIdx % monthly.length] || ytdTotal / 12;
                // Mild per-month variance so the chart doesn't look identical every render.
                const wiggle = 0.85 + ((i * 37) % 30) / 100; // 0.85–1.14 deterministically
                return Math.round(base * 2.1 * wiggle);
            });
        }
        if (period === 'month') {
            return months.map((_m, i) => (i === currentMonthIdx ? mtd : 0));
        }
        if (period === 'lastMonth') {
            const idx = (currentMonthIdx - 1 + 12) % 12;
            return months.map((_m, i) => (i === idx ? Math.round(mtd * 1.12) : 0));
        }
        return monthly;
    })();
    const max = Math.max(...monthlyForPeriod, 1);
    return (
        <CardShell title="Income" period={period} onPeriodChange={setPeriod}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={2}>
                <Box>
                    <Typography variant="h4">{currency(total)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Total Income
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h4">{currency(mtd)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {monthLabel} Total
                    </Typography>
                </Box>
            </Stack>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(12, 1fr)',
                    columnGap: 1,
                    alignItems: 'end',
                    height: 120,
                    pt: 1,
                }}>
                {monthlyForPeriod.map((value, i) => {
                    const showLabel = i % 3 === 0;
                    return (
                        <Stack key={months[i]} alignItems="center" spacing={0.5} sx={{ height: '100%' }}>
                            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'flex-end', width: '100%' }}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: `${(value / max) * 100}%`,
                                        minHeight: value > 0 ? 4 : 2,
                                        bgcolor: value > 0 ? theme.palette.primary.main : 'grey.200',
                                        borderRadius: 0.5,
                                        transition: 'height 0.55s cubic-bezier(0.4, 0, 0.2, 1)',
                                    }}
                                />
                            </Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ visibility: showLabel ? 'visible' : 'hidden' }}>
                                {months[i]}
                            </Typography>
                        </Stack>
                    );
                })}
            </Box>
        </CardShell>
    );
}

export interface FinancialOverviewProps {
    ytdIncome: number;
    ytdExpenses: number;
    mtdIncome: number;
    mtdExpenses: number;
    overview?: OverviewBlob;
}

export function FinancialOverviewTiles({
    ytdIncome,
    ytdExpenses,
    mtdIncome,
    mtdExpenses,
    overview = undefined,
}: FinancialOverviewProps) {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: { xs: 2, md: 3 },
            }}>
            <ProfitLossCard
                ytdIncome={ytdIncome}
                ytdExpenses={ytdExpenses}
                mtdIncome={mtdIncome}
                mtdExpenses={mtdExpenses}
            />
            <CashFlowCard
                ytdCashIn={overview?.cash_in ?? ytdIncome}
                ytdCashOut={overview?.cash_out ?? ytdExpenses}
                mtdIncome={mtdIncome}
                mtdExpenses={mtdExpenses}
            />
            <ExpensesCard ytdTotal={ytdExpenses} mtd={mtdExpenses} categories={overview?.expense_categories ?? []} />
            <IncomeCard
                ytdTotal={ytdIncome}
                mtd={mtdIncome}
                monthly={overview?.monthly_income ?? new Array(12).fill(0)}
            />
        </Box>
    );
}
